import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import SelectField from '../../components/form/select-field';
import { Dealer, DealerLocation } from '../../types';
import { AddLocationAddressDialog, useGetLocationsQuery } from '../location';
import { selectManageShippingLocations } from '../user';

interface StepTwoProps {
  onFormChange: () => void;
  dealers: Dealer[];
}

const StepTwo: React.FC<StepTwoProps> = ({ onFormChange, dealers }) => {
  const { control, setValue, resetField } = useFormContext();
  const deliveryType = useWatch({ control, name: 'deliveryType' });
  const sameAsBilling = useWatch({ control, name: 'sameAsBilling' });
  const dealerId = useWatch({ control, name: 'dealerId' });
  const canManageShippingLocations = useSelector(selectManageShippingLocations);

  // Add state for add shipping address modal
  const [showAddAddressModal, setShowAddAddressModal] = React.useState(false);

  // Fetch locations for the selected dealer
  const {
    data: locations = [],
    isLoading: isLoadingLocations,
    refetch,
  } = useGetLocationsQuery(dealerId, {
    skip: !dealerId || dealerId === 0,
  });

  const handleAddAddress = async (newAddress: DealerLocation) => {
    setShowAddAddressModal(false);

    // Refresh the locations list to include the newly added location
    await refetch();

    // Set the newly added address as the selected option in the dropdown
    setValue('shippingAddressSelect', newAddress.id?.toString());

    // Update the main form's shippingAddress field
    setValue('shippingAddress', {
      id: newAddress.id,
      name: newAddress.name,
      address1: newAddress.address1,
      address2: newAddress.address2,
      city: newAddress.city,
      state: newAddress.state,
      zip: newAddress.zip,
      email: newAddress.email,
      phone: newAddress.phone,
      dealerId: newAddress.dealerId,
    });

    // Trigger form change to re-validate
    onFormChange();
  };

  const handleCloseAddressDialog = () => {
    setShowAddAddressModal(false);
  };

  // If Pickup is selected, clear billing and shipping address fields
  useEffect(() => {
    if (deliveryType === 'Pickup') {
      setValue('sameAsBilling', undefined);
      setValue('shippingAddressSelect', undefined);
      setValue('shippingAddress', undefined);
    }
  }, [deliveryType, setValue]);

  // Watch the selected address ID from the select field
  const selectedAddressId = useWatch({ control, name: 'shippingAddressSelect' });

  // When component mounts or locations load, sync shipping address if there's a selected ID
  useEffect(() => {
    if (selectedAddressId && locations.length > 0) {
      const selected = locations.find(addr => addr.id?.toString() === selectedAddressId);
      if (selected) {
        setValue('shippingAddressSelect', selected.id?.toString());
        setValue('shippingAddress', {
          id: selected.id,
          name: selected.name,
          address1: selected.address1,
          address2: selected.address2,
          city: selected.city,
          state: selected.state,
          zip: selected.zip,
          email: selected.email,
          phone: selected.phone,
          dealerId: selected.dealerId,
        });
        onFormChange();
      } else {
        resetField('shippingAddressSelect');
      }
    }
  }, [locations, selectedAddressId, setValue, onFormChange, resetField]);

  useEffect(() => {
    if (sameAsBilling) {
      // Clear the address dropdown selection
      setValue('shippingAddressSelect', undefined);
      // Populate shippingAddress with the dealer's billing address so Step 3 can prefill
      const selectedDealer = dealers?.find(d => d.id === dealerId);
      if (selectedDealer) {
        setValue('shippingAddress', {
          id: undefined,
          name: selectedDealer.name,
          address1: selectedDealer.address1,
          address2: selectedDealer.address2 ?? undefined,
          city: selectedDealer.city,
          state: selectedDealer.state,
          zip: selectedDealer.zip,
          phone: selectedDealer.phone ?? undefined,
          email: selectedDealer.email ?? undefined,
          dealerId: selectedDealer.id,
        });
      } else {
        setValue('shippingAddress', undefined);
      }
    }
  }, [sameAsBilling, dealerId, dealers, setValue]);

  useEffect(() => {
    onFormChange();
  }, [deliveryType, sameAsBilling, onFormChange]);

  return (
    <>
      {/* Overlay to disable events when modal is open */}
      {showAddAddressModal && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255,255,255,0.2)',
            zIndex: 1300,
          }}
        />
      )}
      {/* Step Two Content */}
      <Grid
        container
        spacing={3}
        style={
          showAddAddressModal ? { pointerEvents: 'none', userSelect: 'none', opacity: 0.5 } : {}
        }
      >
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }} />
            <Typography variant="h6">Shipping Address Details</Typography>
          </Box>
        </Grid>
        {/* Row 1: Delivery or Pickup */}
        <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'flex-start' }}>
          <FormControl component="fieldset">
            <Controller
              name="deliveryType"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} sx={{ flexDirection: 'column' }}>
                  <FormControlLabel value="Delivery" control={<Radio />} label="Delivery" />
                  <FormControlLabel value="Pickup" control={<Radio />} label="Pickup" />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={9} style={{ display: 'flex', alignItems: 'center' }}>
          <Typography>
            Select whether the order will be picked up or needs to be delivered. If it needs to be
            delivered, a Shipping Address will be required.
          </Typography>
        </Grid>

        {/* Only show the rest if not Pickup */}
        {deliveryType !== 'Pickup' && (
          <>
            {/* Row 2: Billing Address Checkbox and Helper Text */}
            <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box>
                <Controller
                  name="sameAsBilling"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label="Billing Address"
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={9} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography>
                Indicate whether the Shipping Address is the same as the Billing Address.
              </Typography>
            </Grid>

            {/* Only show shipping address select and add row if not same as billing */}
            {!sameAsBilling && (
              <>
                {/* Row 3: Shipping Address Select */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SelectField
                      name="shippingAddressSelect"
                      control={control}
                      label={isLoadingLocations ? 'Loading Locations...' : 'Shipping Address'}
                      options={locations.map(addr => ({
                        value: addr.id?.toString() ?? '',
                        label: addr.name,
                      }))}
                      required={true}
                      disabled={isLoadingLocations || !dealerId || dealerId === 0}
                    />
                    {!dealerId && <FormHelperText>Select a dealer first</FormHelperText>}
                    {!!dealerId && locations.length === 0 && !isLoadingLocations && (
                      <FormHelperText>No locations found for this dealer</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Row 4: Add Shipping Address (Supervisors/Admins only) */}
                {canManageShippingLocations && (
                  <>
                    <Grid item xs={12} md={9} style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>
                        Don't see your shipping address here, then add a new one.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowAddAddressModal(true)}
                        fullWidth
                        disabled={!dealerId || dealerId === 0}
                      >
                        Add Shipping Address
                      </Button>
                    </Grid>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Grid>

      {/* Add Location Address Dialog */}
      {dealerId && dealerId !== 0 && (
        <AddLocationAddressDialog
          open={showAddAddressModal}
          onClose={handleCloseAddressDialog}
          onSave={handleAddAddress}
          dealerId={dealerId}
        />
      )}
    </>
  );
};

export default StepTwo;
